<script>

    import {createEventDispatcher, onDestroy} from "svelte";
    import {get} from "svelte/store";
    import QrCode from "svelte-qrcode";
    import CartItem from "./CartItem.svelte";
    import {cartInfoStore, cartItemsStore, cartTotalStore, confirmOrder} from "./cart-store.js";
    import {publishConfigStore, tenantKeyStore} from "../core/app-store.js";
    import {scrollToElementId} from "../core/utils.js";
    import {orderConfigStore} from "../orders/orders-store.js";
    import {closeCartScreen, openOrderScreen, screensStore} from "../core/screens-manager.js";
    import Button from "../components/Button.svelte";
    import {reachGoal} from "../core/ecommerce.js";
    import {deliveryOpenStore, checkOpenState} from "../core/time.js";
    import TotalBlock from "../components/TotalBlock.svelte";
    import {t} from "../core/i18n/language-store.js";

    const orderConfig = get(orderConfigStore);

    const dispatch = createEventDispatcher();

    export let opened = false;
    export let showTotal = true;

    function closeScreen () {
        dispatch('close');
    }

    function confirm (items) {

        confirmOrder(items);
        openOrderScreen();
        reachGoal('OPEN_ORDER_FORM');
    }

    const storeSubscriptions = [
        screensStore.subscribe(x => {
            if (x.cartOpened) {
                checkOpenState();
            }
        })
    ];

    onDestroy(() => {
        for (let i = 0; i < storeSubscriptions.length; i++){
            storeSubscriptions[i]();
        }
    });

</script>

<style>

    .screen-header svg {
        color: var(--screen-text-color);
    }
    .screen-header small {
        opacity: .7;
    }

    ul {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        margin-bottom: .5rem;
    }
    li {}
    li:last-child.separator {
        display: none;
    }
    li.no-data {
        text-align: center;
        padding: 2rem 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
    }
    li.no-data button {
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: var(--border-radius-xl);
        font-size: inherit;
        font-family: inherit;
        font-weight: 600;
        letter-spacing: .5px;
        color: var(--price-item-font-color);
        background: var(--success-bg-color);
        transition: background-color .2s ease-in-out;
        box-shadow: 0 6px 9px 4px rgb(0 0 0 / 8%);
    }

    li.separator {
        border-bottom: 1px solid var(--cart-items-separator);
    }

    .delivery-block {
        text-align: right;
        font-weight: 400;
    }
    .total-block {
        font-size: var(--fs-150);
        font-weight: 600;
        display: flex;
        justify-content: end;
        padding: 5px 0;
    }
    .min-order-note {
        text-align: center;
    }
    .min-order-warning {
        line-height: 1.25;
        background: var(--cart-min-order-warning-bg);
        padding: .5rem .8rem;
        border-radius: var(--border-radius-xs);
    }
    .order-qr {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: var(--bg-second-color);
        margin-top: -2.5rem;
        z-index: 0;
        padding: 2rem 1rem;
        border-radius: var(--border-radius-xxl);
    }
    .order-qr p {
        text-align: center;
    }

    .ask-block {
        color: var(--main-font-color);
        background: var(--bg-second-color);
        border: 1px solid var(--bg-second-color);
        border-radius: .9rem;
    }
</style>

{#if (opened)}

    <div class="layer-cart screen-backdrop"></div>
    <div class="layer-cart screen-content slide-up-animation--centered">
        <div class="screen-header">
            <span>
                <h1>{$t('cart.title')}</h1>
                <small>{ $cartTotalStore.count } {$t('cart.itemsCount')}</small>
            </span>

            <button class="screen-close-button pointer" on:click={closeScreen}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ai ai-Cross"><path d="M20 20L4 4m16 0L4 20"/></svg>
            </button>
        </div>

        <ul>
            {#each $cartItemsStore as item, i}
            <li>
                <CartItem item={item}
                          publishConfig={$publishConfigStore}
                          tenantKey={$tenantKeyStore}>
                </CartItem>
            </li>
            <li class="separator"></li>
            {/each}

            {#if ($cartItemsStore.length === 0)}
                <li class="no-data">
                    <span>{$t('cart.empty')}</span>
                    <button class="pointer" on:click={closeScreen}>{$t('cart.buttons.returnToMenu')}</button>
                </li>
            {/if}
        </ul>

        {#if (showTotal)}
            <TotalBlock hideTotal={true} hideDelivery={true}></TotalBlock>
        {/if}

        <div class="flex-fill"></div>

        <div class="screen-footer">
            {#if ($cartItemsStore.length > 0 && $orderConfigStore.enabled)}

                {#if ($cartTotalStore?.sum < ($orderConfigStore.minOrder || 0) ) }
                    <div class="min-order-warning">
                        {$t('cartOrder.minOrderMessage')} <strong class="price-pair" class:price-pair--rev={$publishConfigStore?.currency === '$'}>
                            <span>{ $orderConfigStore.minOrder }</span>
                            <span>{ $publishConfigStore?.currency || '' }</span>
                        </strong>.
                    </div>
                {/if}

                {#if ($deliveryOpenStore.isOpen === false && $deliveryOpenStore.nextOpenDateStr && $orderConfigStore.outOfOpenHours !== 'ALLOW')}
                    {#if ($orderConfigStore.outOfOpenHours === 'ALLOW_NOTIFY')}
                        <div class="min-order-note">
                            {$t('cartOrder.deliveryClosedMessage')}
                        </div>
                    {/if}
                    {#if ($deliveryOpenStore.waitMinutes <= 60)}
                        <div class="min-order-note">{$t('cartOrder.openInMinutes')} { $deliveryOpenStore.waitMinutes } мин.</div>
                    {:else}
                        <div class="min-order-note">{$t('cartOrder.openAt')} { $deliveryOpenStore.nextOpenDateStr }</div>
                    {/if}
                {/if}

                <Button label={$t('cartOrder.placeOrder')}
                        icon="bag"
                        type="primary"
                        disabled="{$cartTotalStore?.sum < ($orderConfigStore.minOrder || 0) || ($deliveryOpenStore.isOpen === false && $orderConfigStore.outOfOpenHours === 'NOT_ALLOW')}"
                        on:clicked={confirm($cartItemsStore)}>
                </Button>
            {/if}

            {#if ($cartItemsStore.length > 0 && !$orderConfigStore.enabled)}
                <div class="ask-block flex jc-center ai-center gap-1 ptb-1 plr-1">
                    <svg width="31" height="30" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24.9665 0.483195C25.0508 0.361839 25.1582 0.258277 25.2825 0.178426C25.4068 0.0985738 25.5457 0.0439955 25.6911 0.0178085C25.8365 -0.00837855 25.9857 -0.00566143 26.13 0.0258043C26.2744 0.0572701 26.4112 0.116868 26.5325 0.201195C27.7445 1.0427 28.7495 2.5172 29.3795 4.1417C30.0155 5.7827 30.3245 7.71769 29.999 9.56869C29.9477 9.86269 29.7817 10.1243 29.5375 10.2958C29.2933 10.4674 28.991 10.535 28.697 10.4837C28.403 10.4324 28.1414 10.2664 27.9698 10.0222C27.7982 9.77803 27.7307 9.47569 27.782 9.1817C28.022 7.8122 27.8015 6.29569 27.281 4.95469C26.756 3.59569 25.9775 2.55469 25.2485 2.04919C25.1271 1.9649 25.0236 1.85752 24.9437 1.7332C24.8639 1.60887 24.8093 1.47003 24.7831 1.32461C24.7569 1.17919 24.7596 1.03003 24.7911 0.88566C24.8226 0.741289 24.8822 0.60453 24.9665 0.483195ZM11.3975 1.30969C11.0252 0.781806 10.4959 0.384715 9.88499 0.174873C9.27405 -0.0349703 8.6125 -0.0468859 7.9944 0.14082C7.3763 0.328526 6.8331 0.706301 6.44202 1.22044C6.05094 1.73458 5.83188 2.35892 5.81599 3.00469C5.33543 3.00079 4.86086 3.11154 4.43167 3.32774C4.00247 3.54395 3.63102 3.85938 3.34812 4.24787C3.06522 4.63635 2.87904 5.0867 2.80502 5.56154C2.73099 6.03638 2.77127 6.52203 2.92249 6.9782L5.86249 15.8597C4.95757 15.7898 4.04759 15.8915 3.18049 16.1597C2.15632 16.4829 1.24088 17.0818 0.53449 17.8907C0.280801 18.176 0.109063 18.5246 0.0374953 18.8996C-0.0340725 19.2746 -0.00280187 19.662 0.12799 20.0207C0.35749 20.6702 0.86899 21.1907 1.49449 21.4757C3.61699 22.4387 7.90549 24.6527 11.6225 28.2062C13.16 29.6762 15.404 30.4157 17.567 29.7692L21.4475 28.6067C22.1848 28.3906 22.8444 27.967 23.3476 27.3864C23.8508 26.8058 24.1764 26.0927 24.2855 25.3322C24.5015 23.7557 24.7655 21.3707 24.7655 19.1252C24.7655 16.4792 24.0815 13.5632 23.2655 11.0102C22.5789 8.88759 21.7649 6.80832 20.828 4.7837C20.5353 4.15478 20.0387 3.64329 19.4186 3.33231C18.7985 3.02133 18.0916 2.92913 17.4125 3.07069C16.9775 3.15769 16.586 3.3272 16.2455 3.5597L16.013 2.8307C15.869 2.37884 15.62 1.96755 15.2864 1.63055C14.9527 1.29356 14.5439 1.04047 14.0935 0.892044C13.6431 0.743617 13.1639 0.704082 12.6953 0.776681C12.2266 0.849281 11.7819 1.03195 11.3975 1.30969ZM9.66349 2.80969L10.2695 4.57369C10.2835 4.62369 10.2995 4.6737 10.3175 4.7237L12.827 11.9927C12.8852 12.1613 12.9826 12.3137 13.1113 12.4372C13.24 12.5607 13.3962 12.6519 13.567 12.7032C13.7379 12.7545 13.9184 12.7644 14.0939 12.7322C14.2693 12.6999 14.4346 12.6264 14.576 12.5177L14.6 12.4982C14.7791 12.3527 14.9091 12.1556 14.9725 11.9337C15.0358 11.7118 15.0293 11.4758 14.954 11.2577L12.4235 3.9152C12.3757 3.72505 12.4042 3.52373 12.503 3.35437C12.6018 3.18501 12.763 3.06107 12.952 3.0091C13.1411 2.95713 13.343 2.98126 13.5145 3.07632C13.686 3.17138 13.8134 3.32982 13.8695 3.51769L14.9555 6.9182C14.9735 6.9932 14.9945 7.0687 15.0185 7.1447L16.3235 11.1992L16.568 11.9687C16.613 12.1095 16.6853 12.2401 16.7808 12.353C16.8763 12.4659 16.9931 12.5589 17.1245 12.6267C17.2559 12.6944 17.3994 12.7357 17.5467 12.748C17.6941 12.7603 17.8424 12.7435 17.9832 12.6984C18.1241 12.6534 18.2547 12.5811 18.3676 12.4856C18.4805 12.3902 18.5735 12.2734 18.6412 12.142C18.709 12.0105 18.7502 11.8671 18.7625 11.7197C18.7748 11.5724 18.758 11.424 18.713 11.2832L18.4655 10.5137L17.1305 6.3347C17.089 6.0991 17.1415 5.85665 17.2767 5.65927C17.4118 5.46188 17.6189 5.32527 17.8535 5.27869C18.0364 5.23649 18.2282 5.25793 18.3973 5.33948C18.5664 5.42102 18.7026 5.55779 18.7835 5.72719C19.3985 7.06069 20.339 9.2462 21.122 11.6972C21.9095 14.1617 22.517 16.8212 22.517 19.1267C22.517 21.2207 22.268 23.4917 22.0565 25.0292C21.9665 25.6892 21.485 26.2487 20.8025 26.4527L16.9205 27.6137C15.6545 27.9932 14.2265 27.5837 13.178 26.5817C9.20299 22.7792 4.65649 20.4407 2.42449 19.4267C2.36863 19.4011 2.3186 19.3644 2.27749 19.3187C2.62549 18.9362 3.13549 18.5387 3.86599 18.3047C4.63099 18.0602 5.71549 17.9687 7.23199 18.3617C7.42777 18.4124 7.6336 18.4094 7.82783 18.353C8.02205 18.2966 8.19748 18.1889 8.33565 18.0412C8.47382 17.8935 8.56962 17.7113 8.61297 17.5138C8.65632 17.3162 8.64562 17.1107 8.58199 16.9187L5.05849 6.2717C4.99404 6.07696 5.00959 5.8646 5.10172 5.68133C5.19385 5.49806 5.355 5.35889 5.54974 5.29444C5.74447 5.23 5.95684 5.24555 6.14011 5.33767C6.32338 5.4298 6.46254 5.59096 6.52699 5.78569L8.68399 12.3047L9.07099 13.4822C9.11767 13.6226 9.19157 13.7525 9.28844 13.8644C9.38532 13.9763 9.50329 14.068 9.63561 14.1343C9.76794 14.2006 9.91203 14.2402 10.0596 14.2508C10.2073 14.2614 10.3555 14.2429 10.496 14.1962C10.6364 14.1495 10.7663 14.0756 10.8782 13.9787C10.9901 13.8819 11.0818 13.7639 11.1481 13.6316C11.2144 13.4992 11.254 13.3552 11.2646 13.2075C11.2752 13.0599 11.2567 12.9116 11.21 12.7712L10.8215 11.5997L8.10649 3.33469C8.03687 3.12822 8.05212 2.90255 8.14889 2.70733C8.24566 2.5121 8.41602 2.36331 8.62249 2.29369C8.82896 2.22408 9.05463 2.23933 9.24986 2.3361C9.44508 2.43287 9.59387 2.60322 9.66349 2.80969ZM24.464 3.35869C24.2609 3.14009 23.9793 3.01112 23.6811 3.00015C23.3829 2.98917 23.0926 3.09711 22.874 3.30019C22.6554 3.50328 22.5264 3.7849 22.5154 4.08308C22.5045 4.38126 22.6124 4.67159 22.8155 4.8902C23.4425 5.5637 24.014 6.3752 24.014 7.8752C24.014 8.17356 24.1325 8.45971 24.3435 8.67069C24.5545 8.88167 24.8406 9.0002 25.139 9.0002C25.4374 9.0002 25.7235 8.88167 25.9345 8.67069C26.1455 8.45971 26.264 8.17356 26.264 7.8752C26.264 5.6222 25.3295 4.28869 24.4625 3.35719" fill="currentColor"/>
                    </svg>
                    <strong>{ $t('cart.askWaiter') }</strong>
                </div>
            {/if}
        </div>

    </div>
{/if}
